import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import './DeleteAccount.css';

const DeleteAccount = () => {
    const [user, setUser] = useState(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(() => {
        const getSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            setUser(session?.user ?? null);
        };
        getSession();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setUser(session?.user ?? null);
        });

        return () => subscription.unsubscribe();
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) setError(error.message);
        setLoading(false);
    };

    const handleDeleteAccount = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);

        const uid = user.id;
        console.log('Initiating account deletion for UID:', uid);

        try {
            // 1. Delete from 'pedido'
            console.log('Deleting from pedido...');
            const { error: pedidoError } = await supabase
                .from('pedido')
                .delete()
                .eq('idVendedor', uid);
            if (pedidoError) {
                console.error('Error deleting from pedido:', pedidoError);
                throw new Error(`Erro ao excluir pedidos: ${pedidoError.message}`);
            }
            console.log('Pedido deletion successful');

            // 2. Delete from 'produto'
            console.log('Deleting from produto...');
            const { error: produtoError } = await supabase
                .from('produto')
                .delete()
                .eq('idVendedor', uid);
            if (produtoError) {
                console.error('Error deleting from produto:', produtoError);
                throw new Error(`Erro ao excluir produtos: ${produtoError.message}`);
            }
            console.log('Produto deletion successful');

            // 3. Delete from 'profiles'
            console.log('Deleting from profiles...');
            const { error: profileError } = await supabase
                .from('profiles')
                .delete()
                .eq('id', uid);
            if (profileError) {
                console.error('Error deleting from profiles:', profileError);
                throw new Error(`Erro ao excluir perfil: ${profileError.message}`);
            }
            console.log('Profile deletion successful');

            // 4. Delete the user from Supabase Auth via RPC
            console.log('Calling delete_user RPC...');
            const { error: authError } = await supabase.rpc('delete_user');

            if (authError) {
                console.error('RPC delete_user failed:', authError);
                // Fallback: Just sign out if the user doesn't have the RPC set up yet
                // but notify them that the account might still exist in auth.
                await supabase.auth.signOut();
                throw new Error(`Dados excluídos, mas a conta de autenticação ainda existe. Erro: ${authError.message}. Certifique-se de que a função SQL 'delete_user' foi criada.`);
            }

            console.log('Auth user deletion successful');
            await supabase.auth.signOut();
            setSuccess(true);
        } catch (err) {
            console.error('Deletion process failed at some step:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="delete-container">
                <div className="card">
                    <h2>Sua conta foi excluída com sucesso</h2>
                    <p>Lamentamos ver você partir. Todos os seus dados foram removidos dos nossos servidores.</p>
                    <button className="primary-button" onClick={() => window.location.href = '/'}>Voltar ao Início</button>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="delete-container">
                <div className="card">
                    <h2>Autenticação Necessária</h2>
                    <p>Para excluir sua conta, você deve primeiro fazer login para confirmar sua identidade.</p>
                    <form onSubmit={handleLogin}>
                        <div className="input-group">
                            <label>Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                            />
                        </div>
                        <div className="input-group">
                            <label>Senha</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                        </div>
                        {error && <p className="error-message">{error}</p>}
                        <button type="submit" className="primary-button" disabled={loading}>
                            {loading ? 'Entrando...' : 'Entrar'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="delete-container">
            <div className="card">
                <h2>Excluir Conta</h2>
                <p className="warning-text">
                    <strong>Atenção:</strong> Esta ação é permanente e irreversível.
                    Ao excluir sua conta, todos os seus pedidos, produtos e informações de perfil serão removidos para sempre.
                </p>
                <div className="user-info">
                    <p>Autenticado como: <strong>{user.email}</strong></p>
                </div>

                {!showConfirmation ? (
                    <button
                        className="delete-button"
                        onClick={() => setShowConfirmation(true)}
                        disabled={loading}
                    >
                        Desejo excluir minha conta
                    </button>
                ) : (
                    <div className="confirmation-actions">
                        <p>Você tem certeza absoluta?</p>
                        <button
                            className="confirm-button"
                            onClick={handleDeleteAccount}
                            disabled={loading}
                        >
                            {loading ? 'Excluindo...' : 'Sim, confirmar exclusão'}
                        </button>
                        <button
                            className="cancel-button"
                            onClick={() => setShowConfirmation(false)}
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                    </div>
                )}
                {error && <p className="error-message">{error}</p>}
            </div>
        </div>
    );
};

export default DeleteAccount;
